import { useId } from 'react';
import type { CampoCambiato, CartaConferma as DatiCarta } from '@masamune/shared';
import { Icon, type IconName } from '../../components/ui/Icon';
import { campoLungo, testoCampo } from './registroApi';
import { useCarta, type UsoCarta, type VistaRiga } from './carteApi';
import './carte.css';

/**
 * La carta «Modifiche da confermare» nella risposta di MUNE (29/10/2026, fetta 2D «Fa» della
 * Fase 2; os-planning/ai/fase-2-progetto.md, «Interfaccia»). Arriva quando MUNE vuole fare una
 * cosa che cancella, manda una mail o ne tocca più d'una (evento `conferma`): il turno si ferma
 * qui e non si scrive niente finché chi ha chiesto non decide. Le chiamate, le spunte e le
 * parole stanno in carteApi.ts.
 *
 * Computer: il titolo, «Vale fino alle 11:12», una riga per modifica con la spunta (quella della
 * finestra Nuovo task, nt-spunta di components/modals/nuovo-task.css, sempre caricato con
 * ModalsRoot), il prima e dopo dei campi, gli avvisi in grigio, la descrizione intera se parte
 * per mail; in fondo il tasto pieno e «Non fare le modifiche». Fatta: ogni riga «Fatto ·
 * Annulla questa modifica», in fondo «Annulla tutto». Il titolo dice lo stato di adesso
 * (29/10/2026, dalla revisione): «Modifiche fatte», «Modifiche non fatte», «Modifiche scadute».
 *
 * Telefono: le stesse righe con la spunta grande del Nuovo task (nt-m-spunta). I tasti della
 * carta dell'ultima risposta stanno nella barra in fondo al pannello, al posto del campo
 * (BarraCarta), sotto il pollice; le altre li hanno sotto le righe.
 */
export function CartaConferma({ carta, mobile, tastiFuori }: {
  carta: DatiCarta;
  mobile: boolean;
  /** Telefono, carta dell'ultima risposta: «Fai le modifiche» e «Non fare le modifiche» sono nella barra del pannello. */
  tastiFuori: boolean;
}): JSX.Element {
  const k = useCarta(carta);
  return mobile ? <CartaTelefono k={k} tastiFuori={tastiFuori} /> : <CartaComputer k={k} />;
}

/**
 * Al telefono, in fondo al pannello al posto del campo: il tasto pieno e «Non fare le
 * modifiche», uno sopra l'altro, sotto il pollice. Le spunte sono quelle della carta nella chat
 * (useCarteStore). Il pannello la mostra a turno finito, a tastiera chiusa, finché la carta è da
 * decidere.
 */
export function BarraCarta({ carta }: { carta: DatiCarta }): JSX.Element {
  const k = useCarta(carta);
  return (
    <div className="ast-ft cc-barra" role="group" aria-label={k.titolo}>
      {k.problema && <p className="cc-problema" role="alert">{k.problema}</p>}
      <TastiTelefono k={k} />
    </div>
  );
}

const ICONA_SEGNO: Partial<Record<VistaRiga['segno'], IconName>> = { fatta: 'check', annullata: 'undo', no: 'x' };

/** A sinistra di una riga decisa: fatta, annullata, non fatta; vuoto per una riga senza spunta. */
function Segno({ segno }: { segno: VistaRiga['segno'] }) {
  const icona = ICONA_SEGNO[segno];
  return (
    <span className="cc-segno" data-segno={segno} aria-hidden="true">
      {icona && <Icon name={icona} size={13} strokeWidth={2.25} />}
    </span>
  );
}

/** «Scadenza: gio 29/10, poi lun 2/11» in fila; una descrizione su più righe, come nel registro. */
function Campo({ c }: { c: CampoCambiato }) {
  if (!campoLungo(c)) return <p className="cc-campo">{testoCampo(c)}</p>;
  return (
    <div className="cc-campo cc-campo-lungo">
      <span>{c.campo}</span>
      {c.prima != null && <span className="cc-prima">Prima: {c.prima}</span>}
      {/* Il dopo intero: è quello che si sta per approvare. Un testo nuovo non ha un prima, e
          «Dopo:» da solo non direbbe niente (integrazione, 29/10/2026). */}
      <span className="cc-dopo">{c.prima != null ? 'Dopo: ' : ''}{c.dopo ?? 'niente'}</span>
    </div>
  );
}

/** Quello che dice una riga sotto i campi, uguale al computer e al telefono: avvisi e descrizione per mail. */
function Dettagli({ v }: { v: VistaRiga }) {
  return (
    <>
      {v.riga.campi.map((c, i) => <Campo key={`${c.campo}-${i}`} c={c} />)}
      {v.avvisi.map((a, i) => <p key={i} className="cc-avviso">{a}</p>)}
      {v.mail && <p className="cc-mail">{v.mail}</p>}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Computer
// ════════════════════════════════════════════════════════════════════

function CartaComputer({ k }: { k: UsoCarta }) {
  const idTitolo = useId();
  return (
    <div className="cc" role="group" aria-labelledby={idTitolo} data-fase={k.fase}>
      <div className="cc-hd">
        {/* «Modifiche da confermare: …» finché si decide, poi «Modifiche fatte: …» (carteApi.ts, titoloDellaCarta). */}
        <h3 className="cc-titolo" id={idTitolo}>{k.titolo}</h3>
        {/* Cambia quando si decide («Fatte 3 modifiche su 4…», «Scaduta: …»): i lettori di schermo lo dicono. */}
        <p className="cc-stato" role="status">{k.frase}</p>
      </div>
      {k.righe.length > 0 && (
        <ul className="cc-righe">
          {k.righe.map((v) => <RigaComputer key={v.riga.indice} v={v} k={k} />)}
        </ul>
      )}
      <PiedeComputer k={k} />
    </div>
  );
}

function RigaComputer({ v, k }: { v: VistaRiga; k: UsoCarta }) {
  const id = useId();
  const r = v.riga;
  const daAnnullare = v.annulla;
  return (
    <li className="cc-riga" data-spenta={v.spenta ? 'true' : undefined}>
      {v.segno === 'spunta' ? (
        <label className="nt-spunta" title={v.spuntata ? 'Si fa' : 'Non si fa'}>
          <input
            id={id}
            type="checkbox"
            checked={v.spuntata}
            disabled={k.occupata}
            onChange={(e) => k.spunta(r.indice, e.target.checked)}
          />
        </label>
      ) : (
        <Segno segno={v.segno} />
      )}
      <div className="cc-corpo">
        {/* La frase si clicca come la spunta; campi, avvisi e descrizione si possono selezionare e copiare. */}
        {v.segno === 'spunta'
          ? <label className="cc-cosa" htmlFor={id}>{r.cosa}</label>
          : <span className="cc-cosa">{r.cosa}</span>}
        <Dettagli v={v} />
        {v.esito && (
          <p className="cc-esito" data-tono={v.tono ?? undefined}>
            {v.esito}
            {v.avvisati && <span title={v.dettaglioAvvisi ?? undefined}> · {v.avvisati}</span>}
            {daAnnullare && (
              <>
                {' · '}
                <button type="button" className="cc-link" disabled={k.occupata} onClick={() => k.annullaRiga(daAnnullare)}>
                  {k.lavoroDi(daAnnullare) === 'annulla' ? 'Annullo…' : 'Annulla questa modifica'}
                </button>
              </>
            )}
          </p>
        )}
        {v.motivo && <p className="cc-motivo">{v.motivo}</p>}
      </div>
    </li>
  );
}

function PiedeComputer({ k }: { k: UsoCarta }) {
  if (k.fase === 'aperta') {
    return (
      <div className="cc-ft">
        <button type="button" className={`btn btn-sm ${k.tastoElimina ? 'btn-danger' : 'btn-primary'}`} disabled={!k.puoConfermare} onClick={k.conferma}>
          <Icon name="check" size={12} /> {k.lavoro === 'conferma' ? 'Faccio le modifiche…' : k.tasto}
        </button>
        <button type="button" className="btn btn-sm btn-ghost" disabled={k.occupata} onClick={k.rifiuta}>
          Non fare le modifiche
        </button>
        {k.problema && <p className="cc-problema" role="alert">{k.problema}</p>}
      </div>
    );
  }
  if (k.fase === 'fatta' && (k.conAnnullaTutto || k.annulloCarta)) {
    return (
      <div className="cc-ft">
        {k.conAnnullaTutto && (
          <button type="button" className="btn btn-sm btn-ghost" disabled={k.occupata} onClick={k.annullaTutto}>
            <Icon name="undo" size={12} /> {k.lavoro === 'annulla' ? 'Annullo…' : 'Annulla tutto'}
          </button>
        )}
        {k.annulloCarta && <p className="cc-nota" role="status">{k.annulloCarta.frase || 'Annullato'}</p>}
      </div>
    );
  }
  return null;
}

// ════════════════════════════════════════════════════════════════════
// Telefono
// ════════════════════════════════════════════════════════════════════

function CartaTelefono({ k, tastiFuori }: { k: UsoCarta; tastiFuori: boolean }) {
  const idTitolo = useId();
  return (
    <div className="cc cc--m" role="group" aria-labelledby={idTitolo} data-fase={k.fase}>
      <div className="cc-hd">
        <h3 className="cc-titolo" id={idTitolo}>{k.titolo}</h3>
        <p className="cc-stato" role="status">{k.frase}</p>
      </div>
      {k.righe.length > 0 && (
        <div className="cc-righe">
          {k.righe.map((v) => <RigaTelefono key={v.riga.indice} v={v} k={k} />)}
        </div>
      )}
      <PiedeTelefono k={k} tastiFuori={tastiFuori} />
    </div>
  );
}

function RigaTelefono({ v, k }: { v: VistaRiga; k: UsoCarta }) {
  const r = v.riga;
  const daAnnullare = v.annulla;
  return (
    <div className="cc-riga" data-spenta={v.spenta ? 'true' : undefined}>
      {v.segno === 'spunta' ? (
        <button
          type="button"
          className="nt-m-spunta"
          role="checkbox"
          aria-checked={v.spuntata}
          aria-label={r.cosa}
          disabled={k.occupata}
          onClick={() => k.spunta(r.indice, !v.spuntata)}
        >
          <span>{v.spuntata && <Icon name="check" size={14} strokeWidth={2.5} />}</span>
        </button>
      ) : (
        <Segno segno={v.segno} />
      )}
      <div className="cc-corpo">
        <span className="cc-cosa">{r.cosa}</span>
        <Dettagli v={v} />
        {v.esito && (
          <p className="cc-esito" data-tono={v.tono ?? undefined}>
            {v.esito}
            {v.avvisati && <> · {v.avvisati}</>}
          </p>
        )}
        {v.motivo && <p className="cc-motivo">{v.motivo}</p>}
        {daAnnullare && (
          <button type="button" className="btn btn-sm cc-annulla" disabled={k.occupata} onClick={() => k.annullaRiga(daAnnullare)}>
            <Icon name="undo" size={12} /> {k.lavoroDi(daAnnullare) === 'annulla' ? 'Annullo…' : 'Annulla questa modifica'}
          </button>
        )}
      </div>
    </div>
  );
}

function PiedeTelefono({ k, tastiFuori }: { k: UsoCarta; tastiFuori: boolean }) {
  if (k.fase === 'aperta') {
    if (tastiFuori) return null;
    return (
      <div className="cc-ft">
        {k.problema && <p className="cc-problema" role="alert">{k.problema}</p>}
        <TastiTelefono k={k} />
      </div>
    );
  }
  if (k.fase === 'fatta' && (k.conAnnullaTutto || k.annulloCarta)) {
    return (
      <div className="cc-ft">
        {k.conAnnullaTutto && (
          <button type="button" className="btn m-block" disabled={k.occupata} onClick={k.annullaTutto}>
            <Icon name="undo" size={14} /> {k.lavoro === 'annulla' ? 'Annullo…' : 'Annulla tutto'}
          </button>
        )}
        {k.annulloCarta && <p className="cc-nota" role="status">{k.annulloCarta.frase || 'Annullato'}</p>}
      </div>
    );
  }
  return null;
}

/** Il tasto pieno sopra, «Non fare le modifiche» sotto: nella barra del pannello o sotto le righe. */
function TastiTelefono({ k }: { k: UsoCarta }) {
  return (
    <>
      <button type="button" className={`btn ${k.tastoElimina ? 'btn-danger' : 'btn-primary'} m-block`} disabled={!k.puoConfermare} onClick={k.conferma}>
        {k.lavoro === 'conferma' ? 'Faccio le modifiche…' : k.tasto}
      </button>
      <button type="button" className="btn m-block" disabled={k.occupata} onClick={k.rifiuta}>
        Non fare le modifiche
      </button>
    </>
  );
}
